import React, { ComponentType } from 'react';
import { useState, useMemo, useCallback, memo } from 'react';
import { useIntl } from 'react-intl';
import {
  X, MagnifyingGlass, Prohibit, Gift, Clock, HeartStraight, Stethoscope, ForkKnife, ChartLineUp,
  Basketball, Money, Books, PiggyBank, ShieldCheck, Baby, Bicycle, FirstAidKit, House, HandHeart,
  WifiHigh, DeviceMobile, Bus, Umbrella, Buildings,
} from '@phosphor-icons/react';
import { useSearchFilters } from '../../contexts/SearchContext';
import { useFacetsForAttribute } from '../../contexts/FacetsContext';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { toggleFinlandFilter } from '../../redux/features/finlandFilterSlice';
import { useDebounce } from '../../hooks/useDebounce';
import { getFilterModalCategoryColors } from '../../utils/filters/filter-attribute-category';
import { TOP_FILTER_EXCLUDED_COLOR, TOP_FILTER_ONLY_COLOR } from '../../layouts/helpers/topFilterConfig';
import { supportsOnlyFilterMode } from '../../utils/typesense/array-facet-count-fields';
import { FilterSwitch } from '../../design-system/FilterSwitch';
import { companyProfileUrlPath } from '../../utils/routing/company-profile-url-path';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import { formatCompanyLogoAltLabelFromProps } from '../../utils/company/format-company-logo-alt-label-from-props';
import { partitionCompanyFacetsTyomarkkinatoriLast } from '../../utils/company/partition-company-facets-tyomarkkinatori-last';
import { isLikelyMunicipalityOrGovernmentName } from '../../utils/company/is-likely-municipality-or-government-name';
import { AGENCY_COMPANY_IDS } from '../../utils/company/default-company-list-agency-exclusions.generated';
import { Link } from '../Link';
import { Msg } from '../../i18n-review/client/Msg';
import { useNewLocationSystemEnabled } from '../../utils/facets/new-location-system-dev-flag';
import { getRegionTagLabel, getRegionTagType, type RegionTagType } from '../../utils/facets/region-tag-labels';
import { LocationHierarchyPicker } from './LocationHierarchyPicker';
import { FinlandGlobeFlagThumb } from './FinlandGlobeFlagThumb';

/**
 * Shared filter-input building blocks (refinement lists, toggles, sliders) used by
 * AllFiltersModal.tsx. Split out from the old standalone per-category popover
 * (components/FilterModal.tsx, removed) once AllFiltersModal became the only place
 * these render: the popover triggers (`activeFilterModal` state) had nothing left
 * pointing at them.
 */

// Maximum number of facet results shown per filter type; users can search to find more
export const MAX_VISIBLE_FACET_ITEMS = 20;

/** Icon per benefit value, for CustomRefinementList's card variant (used by the Benefits
 * section). Benefit values are free text an LLM extracts from job postings, not a fixed enum, so
 * this only covers the common, well-formed ones actually seen in the data (checked against a
 * live facet count pull, not guessed); anything else falls back to Gift. Matched case-insensitively
 * against the raw facet value. */
const BENEFIT_ICONS: Record<string, ComponentType<{ className?: string; weight?: string }>> = {
  'flexible working hours': Clock,
  'well-being / healthcare wallet': HeartStraight,
  'occupational healthcare': Stethoscope,
  'lunch benefit': ForkKnife,
  'stock options / rsu': ChartLineUp,
  'sports & culture benefit': Basketball,
  'cash bonus': Money,
  'training budget': Books,
  'extra pension': PiggyBank,
  'accident / life insurance': ShieldCheck,
  'child-care voucher': Baby,
  'company bike': Bicycle,
  'extra health insurance': FirstAidKit,
  'remote-work allowance': House,
  'massage benefit': HandHeart,
  'home-internet benefit': WifiHigh,
  'work phone': DeviceMobile,
  'commuter pass': Bus,
  'holiday bonus': Umbrella,
};
/** Exported so the editorial-rail job-page redesign (TEC-512) can reuse the same lookup for its benefit chips instead of duplicating the map. */
export const getBenefitIcon = (value: string): ComponentType<{ className?: string; weight?: string }> =>
  BENEFIT_ICONS[value.toLowerCase()] ?? Gift;

const AGENCY_COMPANY_ID_SET = new Set(AGENCY_COMPANY_IDS.map((id) => id.toLowerCase()));

export const CustomRefinementList = memo(({ attribute, operator, intl, formatMessage, searchQuery, variant = 'list' }: { attribute: string; operator?: 'and' | 'or'; intl: any; formatMessage: any; searchQuery?: string; variant?: 'list' | 'cards' }) => {
  const { filters, setFilters } = useSearchFilters();
  const current = ((filters as Record<string, string[]>)?.[attribute] || []) as string[];
  const excludeKey = `exclude_${attribute}`;
  const excluded = ((filters as Record<string, string[]>)?.[excludeKey] || []) as string[];
  const onlyKey = `only_${attribute}`;
  const isOnlySupported = supportsOnlyFilterMode(attribute);
  const only = ((filters as Record<string, string[]>)?.[onlyKey] || []) as string[];
  const [showAll, setShowAll] = useState(false);

  // Some parents (location, company) already render their own search input and pass
  // searchQuery down. For every other attribute, manage a local search query so users
  // can still find items beyond the visible cap.
  const isSearchControlledByParent = searchQuery !== undefined;
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const effectiveSearchQuery = isSearchControlledByParent ? (searchQuery as string) : internalSearchQuery;

  // Get category-specific color classes
  const categoryColors = getFilterModalCategoryColors(attribute);

  // Use facets from context (populated by SSR, no client-side API calls)
  const { items: facetItems } = useFacetsForAttribute(attribute);
  const hasMoreItems = facetItems.length > 5;
  const needsOwnSearch = !isSearchControlledByParent && facetItems.length > MAX_VISIBLE_FACET_ITEMS;

  // Pre-compute lowercase values once for all items to avoid repeated toLowerCase() calls
  const facetItemsWithLowercase = useMemo(() => {
    return facetItems.map(item => ({
      ...item,
      lowerValue: item.value.toLowerCase(),
      lowerLabel: (item.label || item.value).toLowerCase(),
    }));
  }, [facetItems]);

  // Pre-compute lowercase current/excluded/only sets for O(1) lookups
  const currentLowercaseSet = useMemo(() => new Set(current.map(v => v.toLowerCase())), [current]);
  const excludedLowercaseSet = useMemo(() => new Set(excluded.map(v => v.toLowerCase())), [excluded]);
  const onlyLowercaseSet = useMemo(() => new Set(only.map(v => v.toLowerCase())), [only]);

  // Debounce search query to prevent excessive filtering
  const debouncedSearchQuery = useDebounce(effectiveSearchQuery || '', 200);

  // Hoisted above itemsToRender below (TEC-482: needed there for locale-aware regionTags labels).
  const appLocale: 'en' | 'fi' | 'sv' =
    intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';

  // Filter and sort location facets - memoize expensive calculation
  const processedFacetItems = useMemo(() => {
    if (attribute === 'location') {
      // Filter to only show Finland, Remote, or Global locations
      const allowedKeywords = [
        // Finland
        'finland', 'suomi', 'finländ',
        // Remote
        'remote', 'etätyö', 'etä', 'distans', 'fjärr',
        // Global
        'global', 'maailmanlaajuinen', 'världsomfattande',
      ];
      
      // Exclude "Remote - United States" and its translations
      const excludedPatterns = [
        'remote - united states',
        'etätyö - yhdysvallat',
        'etä - yhdysvallat',
        'distans - usa',
        'fjärr - usa',
        'distans - förenta staterna',
        'fjärr - förenta staterna',
      ];
      
      // Always include active (selected) filters even if they don't match keywords
      let filtered = facetItemsWithLowercase.filter(item => {
        // Always include if it's an active filter (O(1) lookup)
        if (currentLowercaseSet.has(item.lowerValue)) {
          return true;
        }
        
        // Exclude "Remote - United States" and its translations
        if (excludedPatterns.some(pattern => 
          item.lowerValue.includes(pattern) || item.lowerLabel.includes(pattern)
        )) {
          return false;
        }
        
        // Check if value or label contains any of the allowed keywords
        return allowedKeywords.some(keyword => 
          item.lowerValue.includes(keyword) || item.lowerLabel.includes(keyword)
        );
      });
      
      // Apply search query filter if provided (using debounced query)
      if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
        const query = debouncedSearchQuery.toLowerCase().trim();
        filtered = filtered.filter(item => {
          // Always include active filters even when searching
          if (currentLowercaseSet.has(item.lowerValue)) {
            return true;
          }
          // Filter by search query
          return item.lowerValue.includes(query) || item.lowerLabel.includes(query);
        });
      }
      
      // Sort by facet count descending, then alphabetically for ties; restore original item structure
      return filtered
        .sort((a, b) => {
          const countDiff = (b.count ?? 0) - (a.count ?? 0);
          return countDiff !== 0 ? countDiff : (a.label || a.value).localeCompare(b.label || b.value);
        })
        .map(({ lowerValue, lowerLabel, ...item }) => item);
    } else if (attribute === 'companyId') {
      if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
        // Apply search query filter for companies (using debounced query)
        const query = debouncedSearchQuery.toLowerCase().trim();
        const filtered = facetItemsWithLowercase.filter(item => {
          // Always include active filters even when searching
          if (currentLowercaseSet.has(item.lowerValue)) {
            return true;
          }
          // Filter by search query - search in both value and label
          return item.lowerValue.includes(query) || item.lowerLabel.includes(query);
        });
        // Sort alphabetically, restore original item structure, pin Työmarkkinatori last
        const sorted = filtered
          .sort((a, b) => (a.label || a.value).localeCompare(b.label || b.value))
          .map(({ lowerValue, lowerLabel, ...item }) => item);
        return partitionCompanyFacetsTyomarkkinatoriLast(sorted);
      }
      // Default company facet order from Algolia, but Työmarkkinatori always last, and
      // staffing/consulting agencies + likely municipalities filtered out (unless already
      // selected/excluded/only'd, so an existing choice never silently disappears). Users can
      // still find and pick one of these by typing a search query above.
      const defaultFiltered = facetItems.filter((item) => {
        const lowerValue = item.value.toLowerCase();
        if (
          currentLowercaseSet.has(lowerValue) ||
          excludedLowercaseSet.has(lowerValue) ||
          onlyLowercaseSet.has(lowerValue)
        ) {
          return true;
        }
        if (AGENCY_COMPANY_ID_SET.has(lowerValue)) {
          return false;
        }
        return !isLikelyMunicipalityOrGovernmentName(item.label || item.value);
      });
      return partitionCompanyFacetsTyomarkkinatoriLast(defaultFiltered);
    } else {
      // For other attributes, apply search filtering if a query is present
      if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
        const query = debouncedSearchQuery.toLowerCase().trim();
        const filtered = facetItemsWithLowercase.filter(item => {
          // Always include active filters even when searching
          if (currentLowercaseSet.has(item.lowerValue)) {
            return true;
          }
          return item.lowerValue.includes(query) || item.lowerLabel.includes(query);
        });
        return filtered.map(({ lowerValue, lowerLabel, ...item }) => item);
      }
      // Otherwise return facetItems as-is
      return facetItems;
    }
  }, [attribute, facetItems, facetItemsWithLowercase, currentLowercaseSet, excludedLowercaseSet, onlyLowercaseSet, debouncedSearchQuery]);

  // Always use facetItems if available, fallback to current filters for SSR consistency
  // This ensures SSR and client render the same initial state.
  // Cap the expanded list so a single facet can't dump hundreds of rows into the modal;
  // searching (external or internal) narrows the pool so more relevant items stay reachable.
  const itemsToRender = useMemo(() => processedFacetItems.length > 0
    ? (showAll ? processedFacetItems.slice(0, MAX_VISIBLE_FACET_ITEMS) : processedFacetItems.slice(0, 5))
    : current.map((v) => ({
        value: v,
        label: attribute === 'regionTags' ? getRegionTagLabel(v, appLocale) : v,
        count: 0,
        isRefined: true,
        ...(attribute === 'regionTags' ? { regionTagType: getRegionTagType(v) } : {}),
      })), [processedFacetItems, showAll, current, attribute, appLocale]);

  const handleInclude = useCallback((value: string) => {
    // Compare lowercase for case-insensitive matching
    const isSelected = current.some(v => v.toLowerCase() === value.toLowerCase());
    const isExcluded = excluded.some(v => v.toLowerCase() === value.toLowerCase());
    const isOnly = only.some(v => v.toLowerCase() === value.toLowerCase());
    const base = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};
    const nextFilters: Record<string, string[]> = { ...base };

    // Remove from excluded if it was excluded
    if (isExcluded) {
      const remainingExcluded = excluded.filter(v => v !== value);
      if (remainingExcluded.length > 0) {
        nextFilters[excludeKey] = remainingExcluded;
      } else {
        delete (nextFilters as any)[excludeKey];
      }
    }

    // Remove from "only" if it was "only": include/exclude/only stay mutually exclusive per value.
    if (isOnly) {
      const remainingOnly = only.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remainingOnly.length > 0) {
        nextFilters[onlyKey] = remainingOnly;
      } else {
        delete (nextFilters as any)[onlyKey];
      }
    }

    if (!isSelected) {
      // Add value to included
      nextFilters[attribute] = [...current, value];
    } else {
      // Remove value from included; if none left, remove attribute entirely (case-insensitive match)
      const remaining = current.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remaining.length > 0) {
        nextFilters[attribute] = remaining;
      } else {
        delete (nextFilters as any)[attribute];
      }
    }
    const hasAny = Object.keys(nextFilters).length > 0;
    setFilters(hasAny ? nextFilters : {});
  }, [current, excluded, only, filters, attribute, excludeKey, onlyKey, setFilters]);

  const handleExclude = useCallback((value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Compare lowercase for case-insensitive matching, consistent with handleInclude.
    const isSelected = current.some(v => v.toLowerCase() === value.toLowerCase());
    const isExcluded = excluded.some(v => v.toLowerCase() === value.toLowerCase());
    const isOnly = only.some(v => v.toLowerCase() === value.toLowerCase());
    const base = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};
    const nextFilters: Record<string, string[]> = { ...base };

    // Remove from included if it was included; a value can't be both at once.
    if (isSelected) {
      const remaining = current.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remaining.length > 0) {
        nextFilters[attribute] = remaining;
      } else {
        delete (nextFilters as any)[attribute];
      }
    }

    // Remove from "only" if it was "only": include/exclude/only stay mutually exclusive per value.
    if (isOnly) {
      const remainingOnly = only.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remainingOnly.length > 0) {
        nextFilters[onlyKey] = remainingOnly;
      } else {
        delete (nextFilters as any)[onlyKey];
      }
    }

    if (!isExcluded) {
      // Add value to excluded
      nextFilters[excludeKey] = [...excluded, value];
    } else {
      // Remove value from excluded; if none left, remove attribute entirely
      const remainingExcluded = excluded.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remainingExcluded.length > 0) {
        nextFilters[excludeKey] = remainingExcluded;
      } else {
        delete (nextFilters as any)[excludeKey];
      }
    }
    const hasAny = Object.keys(nextFilters).length > 0;
    setFilters(hasAny ? nextFilters : {});
  }, [current, excluded, only, filters, attribute, excludeKey, onlyKey, setFilters]);

  const handleOnly = useCallback((value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const isSelected = current.some(v => v.toLowerCase() === value.toLowerCase());
    const isExcluded = excluded.some(v => v.toLowerCase() === value.toLowerCase());
    const isOnly = only.some(v => v.toLowerCase() === value.toLowerCase());
    const base = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};
    const nextFilters: Record<string, string[]> = { ...base };

    // Remove from included/excluded: a value can only be in one of include/exclude/only at once.
    if (isSelected) {
      const remaining = current.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remaining.length > 0) {
        nextFilters[attribute] = remaining;
      } else {
        delete (nextFilters as any)[attribute];
      }
    }
    if (isExcluded) {
      const remainingExcluded = excluded.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remainingExcluded.length > 0) {
        nextFilters[excludeKey] = remainingExcluded;
      } else {
        delete (nextFilters as any)[excludeKey];
      }
    }

    if (!isOnly) {
      // Add value to "only"
      nextFilters[onlyKey] = [...only, value];
    } else {
      // Remove value from "only"; if none left, remove attribute entirely
      const remainingOnly = only.filter(v => v.toLowerCase() !== value.toLowerCase());
      if (remainingOnly.length > 0) {
        nextFilters[onlyKey] = remainingOnly;
      } else {
        delete (nextFilters as any)[onlyKey];
      }
    }
    const hasAny = Object.keys(nextFilters).length > 0;
    setFilters(hasAny ? nextFilters : {});
  }, [current, excluded, only, filters, attribute, excludeKey, onlyKey, setFilters]);

  // TEC-482: groups regionTags items by type (recruitingRegion / continent / country / a merged
  // "finland" bucket for admin1+city) instead of one flat ~1,200-item list, cheap given the
  // list is already this small per-request (facet counts, not the whole gazetteer). admin1 and
  // city are deliberately merged: no non-Finland admin1/city data is seeded yet, so a
  // country-keyed hierarchy would be empty infrastructure for a case that doesn't exist.
  // Anything without a recognized type (shouldn't happen given seeded data, but never silently
  // drop a real facet value) falls into a trailing unlabeled group.
  const groupedRegionTagItems = useMemo(() => {
    if (attribute !== 'regionTags') return null;
    const order: { key: string; types: RegionTagType[] }[] = [
      { key: 'recruitingRegion', types: ['recruitingRegion'] },
      { key: 'continent', types: ['continent'] },
      { key: 'country', types: ['country'] },
      { key: 'finland', types: ['admin1', 'city'] },
    ];
    const grouped = order.map((group) => ({
      key: group.key,
      items: itemsToRender.filter((item: { regionTagType?: RegionTagType }) =>
        item.regionTagType ? group.types.includes(item.regionTagType) : false
      ),
    }));
    const groupedValues = new Set(grouped.flatMap((g) => g.items.map((item: { value: string }) => item.value)));
    const ungrouped = itemsToRender.filter((item: { value: string }) => !groupedValues.has(item.value));
    return [...grouped, { key: 'other', items: ungrouped }].filter((g) => g.items.length > 0);
  }, [attribute, itemsToRender]);

  const regionTagGroupHeading = (key: string) => {
    switch (key) {
      case 'recruitingRegion':
        return <Msg id="search.refinements.regionTags.group.recruitingRegion" defaultMessage="Regions" description="Location filter group heading for recruiting regions like EMEA/APAC/Nordics" />;
      case 'continent':
        return <Msg id="search.refinements.regionTags.group.continent" defaultMessage="Continents" description="Location filter group heading for continents" />;
      case 'country':
        return <Msg id="search.refinements.regionTags.group.country" defaultMessage="Countries" description="Location filter group heading for countries" />;
      case 'finland':
        return <Msg id="search.refinements.regionTags.group.finland" defaultMessage="Finland" description="Location filter group heading for Finnish regions and cities" />;
      default:
        return null;
    }
  };

  const renderItem = ({ value, label, count }: { value: string; label?: string; count: number }, index: number) => {
        // Use pre-computed sets for O(1) lookups instead of O(n) some() calls
        const valueLower = value.toLowerCase();
        const isIncluded = currentLowercaseSet.has(valueLower);
        const isExcluded = excludedLowercaseSet.has(valueLower);
        const isOnly = onlyLowercaseSet.has(valueLower);

        const rowShellClass = `
              w-full px-4 py-2 rounded-lg transition-colors duration-200 border
              ${
                isIncluded
                  ? `${categoryColors.bg} ${categoryColors.text} ${categoryColors.border} hover:brightness-[0.97] dark:hover:brightness-110`
                  : isExcluded
                  ? `${TOP_FILTER_EXCLUDED_COLOR} hover:brightness-[0.97] dark:hover:brightness-110`
                  : isOnly
                  ? `${TOP_FILTER_ONLY_COLOR} hover:brightness-[0.97] dark:hover:brightness-110`
                  : 'border-[#DDD5C8] dark:border-[#4A3520] hover:bg-[#F2EADF] dark:hover:bg-[#342415] text-[#3D3529] dark:text-[#C4A882] hover:border-[#DDD5C8] dark:hover:border-[#4A3520]'
              }
            `;

        const countBadge = (
          <span
            className={`
                  text-xs px-2 py-1 rounded-full flex-shrink-0
                  ${
                    isIncluded
                      ? categoryColors.badge
                      : isExcluded
                      ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
                      : isOnly
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                      : 'bg-[#DDD5C8] dark:bg-[#4A3520] text-[#3D3529] dark:text-[#C4A882]'
                  }
                `}
          >
            {count > 0 ? count : ''}
          </span>
        );

        const excludeToggle = (
          <button
            suppressHydrationWarning
            onClick={(e) => handleExclude(value, e)}
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-base leading-none transition-colors ${
              isExcluded
                ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60'
                : 'text-[#7A6E5F] dark:text-[#8A7060] hover:bg-[#F2EADF] dark:hover:bg-[#342415] hover:text-red-600 dark:hover:text-red-400'
            }`}
            aria-label={formatMessage(
              isExcluded
                ? {
                    id: 'search.refinements.removeExclude',
                    defaultMessage: 'Remove exclusion of {value}',
                    description: 'Accessibility label for the button that removes a negative (excluded) filter value',
                  }
                : {
                    id: 'search.refinements.exclude',
                    defaultMessage: 'Exclude {value}',
                    description: 'Accessibility label for the button that excludes a filter value from search results',
                  },
              { value }
            )}
            aria-pressed={isExcluded}
          >
            {isExcluded ? <X className="w-4 h-4" weight="bold" /> : <Prohibit className="w-4 h-4" />}
          </button>
        );

        // "Only this value" (exact-match) toggle: offered only for array facets with a
        // companion Count field (see ARRAY_FACET_COUNT_FIELD); meaningless on scalar facets.
        const onlyToggle = !isOnlySupported || (attribute === 'companyId' && shouldLinkCompanyProfile({ companyId: value })) ? null : (
          <button
            suppressHydrationWarning
            onClick={(e) => handleOnly(value, e)}
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-base leading-none transition-colors ${
              isOnly
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60'
                : 'text-[#7A6E5F] dark:text-[#8A7060] hover:bg-[#F2EADF] dark:hover:bg-[#342415] hover:text-blue-600 dark:hover:text-blue-400'
            }`}
            aria-label={formatMessage(
              isOnly
                ? {
                    id: 'search.refinements.removeOnly',
                    defaultMessage: 'Remove "only {value}" filter',
                    description: 'Accessibility label for the button that removes an exact-match (only) filter value',
                  }
                : {
                    id: 'search.refinements.only',
                    defaultMessage: 'Only {value}',
                    description: 'Accessibility label for the button that filters to jobs matching exactly this value and no others',
                  },
              { value }
            )}
            aria-pressed={isOnly}
          >
            =
          </button>
        );

        const labelRow = (
          <span className="text-sm flex items-center min-w-0">
            {isIncluded ? <span className={`mr-2 flex-shrink-0 ${categoryColors.checkmark}`}>✓</span> : null}
            {isExcluded ? <span className="mr-2 flex-shrink-0 text-red-600 dark:text-red-400">✗</span> : null}
            {isOnly ? <span className="mr-2 flex-shrink-0 text-blue-600 dark:text-blue-400">=</span> : null}
            {attribute === 'companyId' && (
              <CompanyLogo
                companyName={value}
                size={24}
                variant="rounded"
                className="mr-2 flex-shrink-0"
                loading="lazy"
                alt={formatCompanyLogoAltLabelFromProps(formatMessage, {
                  companyName: value,
                  companyId: value,
                })}
              />
            )}
            {attribute === 'companyId' ? (
              <CompanyName name={value} />
            ) : (
              label || value
            )}
          </span>
        );

        if (variant === 'cards') {
          const BenefitIcon = getBenefitIcon(value);
          const cardStateClass = isIncluded
            ? `${categoryColors.bg} ${categoryColors.text} ${categoryColors.border}`
            : isExcluded
            ? TOP_FILTER_EXCLUDED_COLOR
            : isOnly
            ? TOP_FILTER_ONLY_COLOR
            : 'border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882]';
          return (
            <div key={value} className={`relative rounded-xl border transition-colors duration-200 ${cardStateClass}`}>
              <button
                suppressHydrationWarning
                onClick={() => handleInclude(value)}
                className="w-full flex items-center gap-2 pl-3 pr-24 py-2.5 text-left cursor-pointer min-w-0"
              >
                <BenefitIcon className="w-5 h-5 shrink-0" weight={isIncluded ? 'fill' : 'regular'} />
                <span className="text-sm min-w-0 flex-1 line-clamp-2">{label || value}</span>
              </button>
              <div className="absolute top-1/2 -translate-y-1/2 right-1.5 flex items-center gap-0.5">
                {count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${isIncluded ? categoryColors.badge : 'bg-[#DDD5C8]/70 dark:bg-[#4A3520]/70 text-[#3D3529] dark:text-[#C4A882]'}`}>
                    {count}
                  </span>
                )}
                {(attribute !== 'companyId' || !shouldLinkCompanyProfile({ companyId: value })) && (
                  <button
                    suppressHydrationWarning
                    onClick={(e) => handleExclude(value, e)}
                    className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                      isExcluded
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                        : 'text-[#9C9184] dark:text-[#8A7060] hover:text-red-600 dark:hover:text-red-400'
                    }`}
                    aria-label={formatMessage(
                      isExcluded
                        ? { id: 'search.refinements.removeExclude', defaultMessage: 'Remove exclusion of {value}', description: 'Accessibility label for the button that removes a negative (excluded) filter value' }
                        : { id: 'search.refinements.exclude', defaultMessage: 'Exclude {value}', description: 'Accessibility label for the button that excludes a filter value from search results' },
                      { value }
                    )}
                    aria-pressed={isExcluded}
                  >
                    {isExcluded ? <X className="w-3 h-3" weight="bold" /> : <Prohibit className="w-3 h-3" />}
                  </button>
                )}
                {isOnlySupported && (attribute !== 'companyId' || !shouldLinkCompanyProfile({ companyId: value })) && (
                  <button
                    suppressHydrationWarning
                    onClick={(e) => handleOnly(value, e)}
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                      isOnly
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                        : 'text-[#9C9184] dark:text-[#8A7060] hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                    aria-label={formatMessage(
                      isOnly
                        ? { id: 'search.refinements.removeOnly', defaultMessage: 'Remove "only {value}" filter', description: 'Accessibility label for the button that removes an exact-match (only) filter value' }
                        : { id: 'search.refinements.only', defaultMessage: 'Only {value}', description: 'Accessibility label for the button that filters to jobs matching exactly this value and no others' },
                      { value }
                    )}
                    aria-pressed={isOnly}
                  >
                    =
                  </button>
                )}
              </div>
            </div>
          );
        }

        if (attribute === 'companyId' && shouldLinkCompanyProfile({ companyId: value })) {
          const profileHref = companyProfileUrlPath(appLocale, value);
          const profileAria = formatMessage(
            {
              id: 'search.refinements.company.openProfileAria',
              defaultMessage:
                'Open company profile for {company}{count, plural, =0 {} other {, {count} open roles on JobCrawls}}',
              description:
                'Accessibility label for company row in filters that navigates to employer profile instead of applying a search filter',
            },
            { company: value, count }
          );
          return (
            <div
              key={value}
              className={rowShellClass.trim()}
              style={
                index > 10
                  ? {
                      contentVisibility: 'auto',
                      containIntrinsicSize: '1px 50px',
                    }
                  : undefined
              }
            >
              <div className="flex items-center justify-between gap-2">
                <Link
                  href={profileHref}
                  locale={appLocale}
                  aria-label={profileAria}
                  className="flex-1 text-left flex items-center gap-2 min-w-0 no-underline"
                >
                  {labelRow}
                </Link>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {excludeToggle}
                  {countBadge}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={value}
            className={rowShellClass.trim()}
            style={index > 10 ? {
              contentVisibility: 'auto',
              containIntrinsicSize: '1px 50px',
            } : undefined}
          >
            <div className="flex items-center justify-between gap-2">
              <button
                suppressHydrationWarning
                onClick={() => handleInclude(value)}
                className="flex-1 text-left flex items-center gap-2 cursor-pointer min-w-0"
              >
                {labelRow}
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                {excludeToggle}
                {onlyToggle}
                {countBadge}
              </div>
            </div>
          </div>
        );
  };

  return (
    <div className="space-y-2">
      {needsOwnSearch && (
        <div className="relative mb-2">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A6E5F] dark:text-[#8A7060] pointer-events-none" />
          <input
            type="text"
            value={internalSearchQuery}
            onChange={(e) => setInternalSearchQuery(e.target.value)}
            placeholder={formatMessage({
              id: 'search.refinements.search.placeholder',
              defaultMessage: 'Search...',
              description: 'Placeholder text for generic facet search input field in filter modal',
            })}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] placeholder-[#7A6E5F] dark:placeholder-[#8A7060] focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:border-transparent transition-colors"
          />
        </div>
      )}
      <div className={variant === 'cards' ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'space-y-2'}>
      {groupedRegionTagItems ? (
        groupedRegionTagItems.map((group) => (
          <div key={group.key} className="space-y-2">
            {group.key !== 'other' && (
              <h4 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#8A7060] px-1">
                {regionTagGroupHeading(group.key)}
              </h4>
            )}
            {group.items.map((item: { value: string; label?: string; count: number }, index: number) => renderItem(item, index))}
          </div>
        ))
      ) : (
        itemsToRender.map((item, index) => renderItem(item, index))
      )}
      </div>
      {hasMoreItems && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full text-center px-4 py-2 text-sm text-[#3B5E45] dark:text-[#98CFA8] hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-colors"
        >
          {showAll ? (
            <Msg id="search.refinements.showLess" defaultMessage="Show less" description="Button to collapse and show fewer filter options" />
          ) : (
            <Msg id="search.refinements.showMore" defaultMessage="Show more" description="Button to expand and show more filter options" />
          )}
        </button>
      )}
    </div>
  );
});

CustomRefinementList.displayName = 'CustomRefinementList';

// LocationFilterSection component with search functionality
export const LocationFilterSection: React.FC<{ intl: any; formatMessage: any }> = memo(({ intl, formatMessage }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const newLocationSystemEnabled = useNewLocationSystemEnabled();

  return (
    <div className="space-y-6">
      {newLocationSystemEnabled ? (
        // TEC-482 new location system: a hierarchical drill-down (LocationHierarchyPicker)
        // instead of CustomRefinementList's old flat regionTags list, pick a broad level
        // (Finland, a recruiting region) or step into it (Finland -> a maakunta -> its cities).
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-[#1E1A14] dark:text-[#F2EADF]">
            <Msg id="search.refinements.regionTags.label" defaultMessage="Region" description="Filter label for hierarchical/recruiting-region location filtering (e.g. APAC, EMEA, Nordics)" />
          </h3>
          {/* Search input for filtering the region list */}
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A6E5F] dark:text-[#8A7060] pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={formatMessage({
                id: 'search.refinements.location.searchPlaceholder',
                defaultMessage: 'Search locations...',
                description: 'Placeholder text for location search input field in filter modal'
              })}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] placeholder-[#7A6E5F] dark:placeholder-[#8A7060] focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:border-transparent transition-colors"
            />
          </div>
          <LocationHierarchyPicker intl={intl} formatMessage={formatMessage} searchQuery={searchQuery} />
        </div>
      ) : (
        <div className="space-y-4">
          <FinlandFilterToggle />
          {/* Search input for filtering locations */}
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A6E5F] dark:text-[#8A7060] pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={formatMessage({
                id: 'search.refinements.location.searchPlaceholder',
                defaultMessage: 'Search locations...',
                description: 'Placeholder text for location search input field in filter modal'
              })}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] placeholder-[#7A6E5F] dark:placeholder-[#8A7060] focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:border-transparent transition-colors"
            />
          </div>
          <CustomRefinementList attribute="location" intl={intl} formatMessage={formatMessage} searchQuery={searchQuery} />
        </div>
      )}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-[#1E1A14] dark:text-[#F2EADF]">
          <Msg id="search.refinements.remoteness.heading" defaultMessage="Remoteness" description="Filter section heading for work model options (onsite, remote, hybrid)" />
        </h3>
        <CustomRefinementList attribute="remoteness" intl={intl} formatMessage={formatMessage} />
      </div>
    </div>
  );
});

LocationFilterSection.displayName = 'LocationFilterSection';

export const BooleanToggle = ({ attribute, label }: { attribute: string; label: React.ReactNode }) => {
  const { filters, setFilters } = useSearchFilters();
  const current = ((filters as Record<string, string[]>)?.[attribute] || []) as string[];
  const checked = current.includes('true');
  const labelId = `filter-toggle-${attribute.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  const apply = (nextChecked: boolean) => {
    const next = nextChecked ? ['true'] : [];
    const nextFilters = { ...(filters as Record<string, string[]>), [attribute]: next.length ? next : undefined } as Record<string, string[]>;
    Object.keys(nextFilters).forEach((k) => {
      if (nextFilters[k] === undefined) delete (nextFilters as Record<string, unknown>)[k];
    });
    setFilters(nextFilters);
  };

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span id={labelId} className="min-w-0 flex-1 text-sm text-jc-ink-secondary">
        {label}
      </span>
      <FilterSwitch checked={checked} onCheckedChange={apply} aria-labelledby={labelId} />
    </div>
  );
};

// Like BooleanToggle, but hides jobs where the attribute is true instead of narrowing to
// them: writes `exclude_${attribute}`, which the generic facetFilters/Typesense layer
// already turns into `NOT attribute:true` (see contexts/SearchContext.tsx, filter_by
// exclude clause in utils/typesense/build-typesense-search-params.ts).
export const ExcludeBooleanToggle = ({ attribute, label }: { attribute: string; label: React.ReactNode }) => {
  const { filters, setFilters } = useSearchFilters();
  const excludeKey = `exclude_${attribute}`;
  const current = ((filters as Record<string, string[]>)?.[excludeKey] || []) as string[];
  const checked = current.includes('true');
  const labelId = `filter-toggle-${excludeKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  const apply = (nextChecked: boolean) => {
    const next = nextChecked ? ['true'] : [];
    const nextFilters = { ...(filters as Record<string, string[]>), [excludeKey]: next.length ? next : undefined } as Record<string, string[]>;
    Object.keys(nextFilters).forEach((k) => {
      if (nextFilters[k] === undefined) delete (nextFilters as Record<string, unknown>)[k];
    });
    setFilters(nextFilters);
  };

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span id={labelId} className="min-w-0 flex-1 text-sm text-jc-ink-secondary">
        {label}
      </span>
      <FilterSwitch checked={checked} onCheckedChange={apply} aria-labelledby={labelId} />
    </div>
  );
};

// Like ExcludeBooleanToggle, but for excluding one specific value out of a multi-value facet
// (e.g. jobType:Freelance) instead of a boolean true/false facet. Writes `exclude_${attribute}`
// with just this value appended/removed, preserving any other excluded values already set on
// the same attribute (e.g. from CustomRefinementList's per-item exclude button on the same facet).
export const ExcludeValueToggle = ({ attribute, value, label }: { attribute: string; value: string; label: React.ReactNode }) => {
  const { filters, setFilters } = useSearchFilters();
  const excludeKey = `exclude_${attribute}`;
  const current = ((filters as Record<string, string[]>)?.[excludeKey] || []) as string[];
  const checked = current.includes(value);
  const labelId = `filter-toggle-${excludeKey.replace(/[^a-zA-Z0-9_-]/g, '-')}-${value.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  const apply = (nextChecked: boolean) => {
    const next = nextChecked ? [...current, value] : current.filter((v) => v !== value);
    const nextFilters = { ...(filters as Record<string, string[]>), [excludeKey]: next.length ? next : undefined } as Record<string, string[]>;
    Object.keys(nextFilters).forEach((k) => {
      if (nextFilters[k] === undefined) delete (nextFilters as Record<string, unknown>)[k];
    });
    setFilters(nextFilters);
  };

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span id={labelId} className="min-w-0 flex-1 text-sm text-jc-ink-secondary">
        {label}
      </span>
      <FilterSwitch checked={checked} onCheckedChange={apply} aria-labelledby={labelId} />
    </div>
  );
};

// Finland filter toggle component with morphing icon
export const FinlandFilterToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const isFinlandEnabled = useAppSelector((state) => state.finlandFilter?.enabled ?? true);

  const finlandLabelId = 'filter-toggle-finland-only';

  return (
    <div className="flex items-center justify-between gap-4 py-2 mb-4 px-1">
      <span id={finlandLabelId} className="min-w-0 flex-1 text-sm font-medium text-jc-ink-secondary">
        <Msg id="search.refinements.showJobsInFinlandOnly" defaultMessage="Show jobs in Finland only" description="Toggle label to filter jobs to only show positions in Finland" />
      </span>
      <FilterSwitch
        checked={isFinlandEnabled}
        onCheckedChange={() => dispatch(toggleFinlandFilter())}
        aria-labelledby={finlandLabelId}
        thumbContent={<FinlandGlobeFlagThumb finlandActive={isFinlandEnabled} />}
      />
    </div>
  );
};

// DiscreteSlider for company headcount (and similar numeric facets). Styled to match the
// salary RangeSlider (components/RangeSlider.tsx) so the two sliders in AllFiltersModal read as
// one design language rather than two: same card chrome, same current-value pill, same
// custom-styled (not browser-default) track.
export const DiscreteSlider = ({ attribute, values, label }: { attribute: string; values: number[]; label: string }) => {
  const { filters, setFilters } = useSearchFilters();
  const intl = useIntl();
  const sliderColors = getFilterModalCategoryColors(attribute);
  const current = ((filters as Record<string, string[]>)?.[attribute] || []) as string[];
  const currentMax = current.length ? Number(current[0]) : values[values.length - 1];
  const [sliderIndex, setSliderIndex] = React.useState(() => {
    const idx = values.findIndex((v) => v >= currentMax);
    return idx >= 0 ? idx : values.length - 1;
  });
  React.useEffect(() => {
    const idx = values.findIndex((v) => v >= currentMax);
    if (idx >= 0 && idx !== sliderIndex) setSliderIndex(idx);
  }, [currentMax]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const idx = Number(e.target.value);
    setSliderIndex(idx);
    const nextVal = values[idx];
    const nextFilters = { ...(filters as Record<string, string[]>), [attribute]: [String(nextVal)] } as Record<string, string[]>;
    setFilters(nextFilters);
  };

  return (
    <div className="w-full rounded-2xl border border-jc-border bg-jc-surface px-3.5 py-3">
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <span className="text-sm font-medium text-jc-ink-secondary">{label}</span>
        <span
          className={`text-sm font-semibold px-2.5 py-1 rounded-full border ${sliderColors.bg} ${sliderColors.text} ${sliderColors.border}`}
        >
          {intl.formatNumber(values[sliderIndex])}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={values.length - 1}
        step={1}
        value={sliderIndex}
        onChange={handleChange}
        aria-label={label}
        className="ios18-slider"
        style={{
          WebkitAppearance: 'none',
          width: '100%',
          height: 6,
          borderRadius: 8,
          background: '#DDD5C8',
          outline: 'none',
        }}
      />
      <div className="flex items-end justify-between mt-2">
        {values.map((v, i) => (
          <div key={v} className="flex flex-col items-center gap-1">
            {/* Icon grows with each step so the headcount scale (10 -> 100,000) reads visually,
                not just numerically. */}
            <Buildings
              size={13 + i * 3}
              weight={i === sliderIndex ? 'fill' : 'regular'}
              className={i === sliderIndex ? sliderColors.text : 'text-jc-muted'}
            />
            <span
              className={`text-xs ${i === sliderIndex ? `${sliderColors.text} font-semibold` : 'text-jc-muted'}`}
            >
              {intl.formatNumber(v)}
            </span>
          </div>
        ))}
      </div>
      {/* Same iOS18-style thumb/track as RangeSlider's .ios18-slider (components/RangeSlider.tsx);
          duplicated rather than shared because the two sliders never render into the same bundle
          chunk-split boundary at once, and a shared style helper would be a bigger refactor than
          this component warrants. */}
      <style>{`
        input[type='range'].ios18-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 8px rgba(30,26,20,0.12);
          border: 2px solid #3B5E45;
          cursor: pointer;
          transition: box-shadow 0.2s, border 0.2s, background 0.2s;
          margin-top: -8px;
        }
        input[type='range'].ios18-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 6px rgba(59, 94, 69, 0.22);
          border: 2px solid #3B5E45;
        }
        input[type='range'].ios18-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 8px rgba(30,26,20,0.12);
          border: 2px solid #3B5E45;
          cursor: pointer;
          transition: box-shadow 0.2s, border 0.2s, background 0.2s;
        }
        input[type='range'].ios18-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 6px rgba(59, 94, 69, 0.22);
          border: 2px solid #3B5E45;
        }
        input[type='range'].ios18-slider::-ms-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 2px 8px rgba(30,26,20,0.12);
          border: 2px solid #3B5E45;
          cursor: pointer;
          transition: box-shadow 0.2s, border 0.2s, background 0.2s;
        }
        input[type='range'].ios18-slider:focus::-ms-thumb {
          box-shadow: 0 0 0 6px rgba(59, 94, 69, 0.22);
          border: 2px solid #3B5E45;
        }
        input[type='range'].ios18-slider::-webkit-slider-runnable-track {
          height: 6px;
          border-radius: 8px;
          background: #C8DDCE;
        }
        input[type='range'].ios18-slider::-ms-fill-lower {
          background: #C8DDCE;
          border-radius: 8px;
        }
        input[type='range'].ios18-slider::-ms-fill-upper {
          background: #C8DDCE;
          border-radius: 8px;
        }
        input[type='range'].ios18-slider:focus {
          outline: none;
        }
        .dark input[type='range'].ios18-slider::-webkit-slider-thumb {
          background: #1F1509;
          border: 2px solid #98CFA8;
        }
        .dark input[type='range'].ios18-slider:focus::-webkit-slider-thumb {
          border: 2px solid #98CFA8;
          box-shadow: 0 0 0 6px rgba(152, 207, 168, 0.18);
        }
        .dark input[type='range'].ios18-slider::-webkit-slider-runnable-track {
          background: #1A2E22;
        }
        .dark input[type='range'].ios18-slider::-ms-fill-lower {
          background: #1A2E22;
        }
        .dark input[type='range'].ios18-slider::-ms-fill-upper {
          background: #1A2E22;
        }
        .dark input[type='range'].ios18-slider::-moz-range-thumb {
          background: #1F1509;
          border: 2px solid #98CFA8;
        }
        .dark input[type='range'].ios18-slider:focus::-moz-range-thumb {
          border: 2px solid #98CFA8;
          box-shadow: 0 0 0 6px rgba(152, 207, 168, 0.18);
        }
        .dark input[type='range'].ios18-slider::-moz-range-track {
          background: #1A2E22;
        }
        .dark input[type='range'].ios18-slider::-ms-thumb {
          background: #1F1509;
          border: 2px solid #98CFA8;
        }
        .dark input[type='range'].ios18-slider:focus::-ms-thumb {
          border: 2px solid #98CFA8;
          box-shadow: 0 0 0 6px rgba(152, 207, 168, 0.18);
        }
        .dark input[type='range'].ios18-slider:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
};

